import type {Translator} from '@logistics-frontend/core/i18n'
import type {Optional} from '@logistics-frontend/types'
import {
  applySpec,
  equals,
  flow,
  get,
  ifElse,
  invert,
  map,
  merge,
  pipe,
  toDefaultEmptyString,
  toUndefined,
} from '@logistics-frontend/utils'

import type {DestinationPoint, RequestBuyout, SddCargoPointAddress, SddClaimRoutePoint} from 'src/shared/types'
import {ParcelPaymentType, PaymentOnDemandType} from 'src/shared/types'
import {getPointId} from 'src/shared/utils'

import {dropEmptyValues} from './drop-empty-values'
import {fromPointAddressToAddress} from './from-point-address'
import {fromPointContact} from './from-point-contact'
import {getPointType} from './get-point-type'
import {modifyPointAddressComment} from './modify-point-address-comment'

const getDestinationPointAddress: (point: DestinationPoint) => Optional<SddCargoPointAddress> = flow(
  get('address'),
  modifyPointAddressComment,
  fromPointAddressToAddress,
  dropEmptyValues,
)

const isSmsConfirmationSkipped: (point: DestinationPoint) => boolean = flow(
  get('contact.isSmsConfirmationEnabled'),
  invert,
)

const isPaymentOnDemand: (point: DestinationPoint) => boolean = flow(
  get('parcels[0].payment'),
  equals(ParcelPaymentType.OnDemand),
)

const toPaymentMethod = (): RequestBuyout => ({
  payment_method: PaymentOnDemandType.Cash,
})

const toPaymentOnDelivery: (point: DestinationPoint) => Optional<RequestBuyout> = ifElse(
  isPaymentOnDemand,
  toPaymentMethod,
  toUndefined,
)

const toSddClaimRoutePoint: (point: DestinationPoint) => SddClaimRoutePoint = applySpec<SddClaimRoutePoint>({
  point_id: getPointId,
  address: getDestinationPointAddress,
  skip_confirmation: isSmsConfirmationSkipped,
  type: getPointType,
  external_order_id: flow(get('externalID'), toDefaultEmptyString),
  payment_on_delivery: toPaymentOnDelivery,
})

function fromSddExpressDestinationPoint(t: Translator) {
  return function (point: DestinationPoint): SddClaimRoutePoint {
    return merge(toSddClaimRoutePoint(point), {
      contact: pipe(point, fromPointContact(t)),
    })
  }
}

export function fromSddExpressDestinationPoints(
  t: Translator,
): (destinationPoints: DestinationPoint[]) => SddClaimRoutePoint[] {
  return map(fromSddExpressDestinationPoint(t))
}
